// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld/base/core';
import Promise from 'bluebird';
import $ from 'jquery';
import _ from 'underscore';
import L from 'leaflet';
import timer from './timer';
import RedoStack from './redoStack';
import convertMultiLangString from './convertMultiLangString';

function _createStyleElement(rulesStr, title) {
    const styleEl = document.createElement('style');
    styleEl.type = 'text/css';

    if (title) styleEl.title = title;

    if (styleEl.styleSheet) styleEl.styleSheet.cssText = rulesStr;
    else styleEl.appendChild(document.createTextNode(rulesStr));

    return styleEl;
}

// Check whether the app is running on Android and version is less than the
// required version specified as an array (e.g. version 4.2.2 would
// be specified as [4,2,2])
const isAndroidAndVersionIsLessThan = requiredVersion => {
    const ua = navigator.userAgent.toLowerCase();
    const androidIndex = ua.indexOf('android');
    if (androidIndex == -1) {
        return false;
    }

    let semiColonPos = ua.indexOf(';', androidIndex);
    if (semiColonPos == -1) {
        // Not found
        semiColonPos = null; // So take remainder of string when slicing below
    }
    const androidVersion = ua.slice(ua.indexOf('android') + 8, semiColonPos);
    const actualVersion = androidVersion.split('.');
    // Compare versions
    if (actualVersion[0] > requiredVersion[0]) {
        return false;
    }
    if (actualVersion[0] == requiredVersion[0]) {
        if (actualVersion[1] > requiredVersion[1]) {
            return false;
        }
        if (actualVersion[1] == requiredVersion[1]) {
            if (actualVersion[2] >= requiredVersion[2]) {
                return false;
            }
        }
    }
    return true;
};

/**
 * Module with a set of generic utility functions and classes used internally in myWorld
 * @exports Util
 * @example
 * myw.Util.loadStyleSheet('mystyles.css')
 */
myw.Util = {
    isIOS: navigator.userAgent.match(/(iPad|iPhone|iPod)/g),

    RedoStack: RedoStack,
    convertMultiLangString: convertMultiLangString,

    timer: timer,

    /**
     * Loads a css file. <br/>
     * Consider the alternative method of loading the file using requirejs and then loading into the dom using loadStyleRules()
     * @param  {string}         path            Url to the file
     * @param  {domElement}     [styleElement]  Style element to add to the dom and check. Alternative argument to giving an url
     * @return {Promise}             Resolved when the style is confirmed to have loaded
     */
    loadStyleSheet: function loadStyleSheet(path, styleElement) {
        //taken from: http://thudjs.tumblr.com/post/637855087/stylesheet-onload-or-lack-thereof
        //adapted to return a promise instead of calling a callback
        return new Promise((resolve, reject) => {
            const // reference to document.head for appending/ removing link nodes
                head = document.getElementsByTagName('head')[0]; // create the link node

            let styleEl;

            if (path) {
                styleEl = document.createElement('link');
                styleEl.setAttribute('href', path);
                styleEl.setAttribute('rel', 'stylesheet');
                styleEl.setAttribute('type', 'text/css');
            } else if (styleElement) {
                styleEl = styleElement;
            }

            let sheet, cssRules;
            // get the correct properties to check for depending on the browser
            if ('sheet' in styleEl) {
                sheet = 'sheet';
                cssRules = 'cssRules';
            } else {
                sheet = 'styleSheet';
                cssRules = 'rules';
            }

            const // how often to check if the stylesheet is loaded
                interval_id = setInterval(() => {
                    // start checking whether the style sheet has successfully loaded
                    try {
                        if (styleEl[sheet] && styleEl[sheet][cssRules].length) {
                            // SUCCESS! our style sheet has loaded
                            clearInterval(interval_id); // clear the counters
                            clearTimeout(timeout_id);
                            resolve(styleEl);
                        }
                    } catch (e) {
                    } finally {
                    }
                }, 10),
                timeout_id = setTimeout(() => {
                    // start counting down till fail
                    clearInterval(interval_id); // clear the counters
                    clearTimeout(timeout_id);
                    head.removeChild(styleEl); // since the style sheet didn't load, remove the link node from the DOM
                    reject();
                }, 15000); // how long to wait before failing

            head.appendChild(styleEl); // insert the link node into the DOM and start loading the style sheet
        });
    },

    /**
     * Loads a string with css rules into the DOM
     * @param  {string} styleRules The css rules to load
     * @param  {string} [title]    Optional title for the set of rules
     * @return {Promise}
     */
    loadStyleRules(styleRules, title) {
        const // reference to document.head for appending/ removing link nodes
            head = document.getElementsByTagName('head')[0];

        let styleRulesStr = '';

        if (typeof styleRules == 'string') {
            styleRulesStr = styleRules;
        } else {
            _.each(styleRules, ruleString => {
                styleRulesStr += ' ' + ruleString;
            });
        }

        //handle relative paths
        if (myw.baseUrl) styleRulesStr = styleRulesStr.replace('url("', 'url("' + myw.baseUrl);

        const styleEl = _createStyleElement(styleRulesStr, title);

        head.appendChild(styleEl);

        return Promise.resolve(styleEl);
    },

    /**
     * Loads an html file into a DOM element
     * @param  {string} url            Url to the html file
     * @param  {string} jquerySelector Selector that identifies an element of the DOM
     * @return {Promise}                Resolved when the file has loaded
     */
    loadInto(url, jquerySelector) {
        return new Promise((resolve, reject) => {
            $(jquerySelector).load(url, null, (responseText, textStatus, XMLHttpRequest) => {
                if (textStatus == 'success') {
                    resolve($(jquerySelector));
                } else {
                    reject(
                        new Error(
                            "Couldn't load " + JSON.stringify(url) + '. Status: ' + textStatus
                        )
                    );
                }
            });
        });
    },

    readCookie(name) {
        const value = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
        return value ? decodeURIComponent(value[2]) : null;
    },
    writeCookie(name, value, timeout_hours) {
        const expires = new Date(),
            expires_milliseconds = 1000 * 60 * 60 * (timeout_hours || 0);
        expires.setTime(expires.getTime() + expires_milliseconds);
        let expiresStr = timeout_hours ? (expiresStr = ';expires=' + expires.toUTCString()) : '';
        document.cookie = name + '=' + encodeURIComponent(value) + ';path=/' + expiresStr;
    },

    loadPlatformSpecificMapStyles() {
        if (isAndroidAndVersionIsLessThan([4, 4, 2])) {
            return myw.Util.loadStyleRules([
                'img.leaflet-tile {outline: 1px solid transparent;}',
                '.ui-dialog-content {-webkit-transform: translate3d(0,0,0); transform: translate3d(0, 0, 0);'
            ]);
        }
        return Promise.resolve();
    },

    /**
     * Returns the format of a (png or jpeg) image
     * @param  {string}     image64     Base64 encoded image
     * @return {string}                 'jpg' or 'png'. Returns null if not a jpg or png image
     */
    getImageFormatFor(image64) {
        const first4 = image64.substring(0, 4); //the first 4 characters in base64 correspond to the first 3 bytes in binary
        if ('/9gA' <= first4 && first4 <= '/9j/') return 'jpg';
        else if (first4 == 'iVBO') return 'png';
        else return null; //could not identify the image format
    },

    /*
     * Calculates and Returns the time since the date supplied as a translated string
     * @private
     */
    timeSince(date) {
        const seconds = Math.floor((new Date() - date) / 1000);

        let interval = Math.floor(seconds / 31536000);

        if (interval > 1) {
            return myw.msg('TimeSince', 'years_since', { count: interval });
        }
        interval = Math.floor(seconds / 2592000);
        if (interval > 1) {
            return myw.msg('TimeSince', 'months_since', { count: interval });
        }
        interval = Math.floor(seconds / 86400);
        if (interval > 1) {
            return myw.msg('TimeSince', 'days_since', { count: interval });
        }
        interval = Math.floor(seconds / 3600);
        if (interval > 1) {
            return myw.msg('TimeSince', 'hours_since', { count: interval });
        }
        interval = Math.floor(seconds / 60);
        if (interval > 1) {
            return myw.msg('TimeSince', 'minutes_since', { count: interval });
        }
        return myw.msg('TimeSince', 'seconds_since', { count: Math.floor(seconds) });
    },

    /**
     * Converts the date to the Local date and time
     * @param  {string} rawDate        Date and time in UTC format
     * @param  {boolean}  withoutTime  If true, return date without the time
     * @return {string}                Local Date and time string in the local format
     *                                 dd month(short) yyyy, hh:mm (AM/PM)
     */
    formatDate(rawDate, withoutTime) {
        const date = new Date(this.formatRawDate(rawDate));
        const dateString = date.toLocaleDateString(navigator.language, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
        const time = this.formatTime(date);
        if (withoutTime) return dateString;
        else return dateString + ', ' + time;
    },

    /*
     * Formats the UTC time so it can be accepted by the javascript Date object in IE8 as well
     * @param  {datetime} rawDate UTC datetime
     * @return {string}           UTC date and time string in 'yyyy/mm/dd hh:mm:ss UTC' format
     */
    formatRawDate(rawDate) {
        //Replacing the 'T' used in the ISO format with space
        return (
            rawDate
                .split('.')[0]
                .replace(/-/g, '/')
                .replace('T', ' ') + ' UTC'
        );
    },

    /*
     * Extracts the time from the datetime string and formats it
     * @param  {string} date UTC date and time string
     * @return {string}      time string in 'hh:mm (AM/PM)' format
     */
    formatTime(date) {
        const hh = date.getHours();
        let m = date.getMinutes();
        let dd = 'AM';
        let h = hh;
        if (h >= 12) {
            h = hh - 12;
            dd = 'PM';
        }
        if (h === 0) {
            h = 12;
        }
        m = m < 10 ? '0' + m : m;

        return h + ':' + m + ' ' + dd;
    },

    /**
     * Checks if a set of search terms partially match any of the terms on a second set
     * @param  {string} searchTerms  Terms to check if they partially at least one of the terms in the second set
     * @param  {string} idTerms      Terms to match against
     * @return {boolean|string}     False if the terms didn't match or the remaining terms if it did
     * @private
     */
    termsMatch(searchTerms, idTerms) {
        if (!idTerms) return false;

        let matches = false;
        const remainingTerms = [];

        const matchesIdTerm = (searchTerm, idTerm) => idTerm.indexOf(searchTerm) >= 0;

        idTerms = idTerms
            .toLowerCase()
            .trim()
            .split(' ');
        searchTerms = searchTerms.trim().split(' ');
        for (let i = 0; i < searchTerms.length; i++) {
            const searchTerm = searchTerms[i].toLowerCase();
            if (_.any(idTerms, matchesIdTerm.bind(null, searchTerm))) {
                matches = true;
                //we don't want the idTerm that was matched to exclude any more search terms
                idTerms = _.reject(idTerms, matchesIdTerm.bind(null, searchTerm));
            } else {
                remainingTerms.push(searchTerms[i]); //return original terms (not lowercased)
            }
        }

        if (matches) return remainingTerms.join(' ');
        else return false;
    },

    /**
     * Returns the union of a list of arrays without exceding a given number of elements
     * If the total number of elements exceeds the given maximum, then elements similar
     * number of elements is taken from each arrays.
     * @param  {Array<Array>} arrays
     * @param  {integer} maxElements
     * @return {Array}
     */
    sampledUnion(arrays, maxElements) {
        const slicedArrays = [];
        let nElems = 0;
        let row = 0;
        const lengthSum = _.reduce(arrays, (memo, arr) => memo + arr.length, 0);

        maxElements = Math.min(maxElements, lengthSum);

        //get one element out of each array until we have enough elements
        while (nElems < maxElements) {
            for (let arrayId = 0; arrayId < arrays.length; arrayId++) {
                const array = arrays[arrayId];
                if (row === 0) slicedArrays[arrayId] = [];

                if (nElems < maxElements && row < array.length) {
                    nElems++;
                    slicedArrays[arrayId].push(array[row]);
                }
            }
            row++;
        }
        return _.flatten(slicedArrays, true);
    },

    /**
     * Safer alternative to eval() that will only evaluate global variables and property accessors
     * @param  {string} expr Expression to be evaluated. ex: 'myw.MywVectorLayer'
     * @param  {object} [context] Object on which to begin looking for the first property name. Defaults to window/global
     */
    evalAccessors(expr, context, executeFunctions) {
        if (typeof expr != 'string') return undefined;
        const parts = expr.split('.');
        const g = context || (typeof global != 'undefined' ? global : window);

        return _.reduce(
            parts,
            (current, part) => {
                if (executeFunctions) return current && _.result(current, part);
                else return current && current[part];
            },
            g
        );
    },

    /**
     * Parses an option object converting expressions stored in string properties to proper values
     * Valid expressions are the ones that can be parsed with myw.Util.evalAccessors and are
     * identified by starting with 'lambda:'
     * @param  {object} jsonObj An object obtained from parsing json
     * @return {object}
     */
    processOptionsFromJson(jsonObj) {
        _.each(jsonObj, (item, key) => {
            if (typeof item == 'string' && item.indexOf('lambda:') === 0) {
                const expr = item.slice(7);
                jsonObj[key] = myw.Util.evalAccessors(expr);
            }
        });
        return jsonObj;
    },

    /**
     * Wraps methods with additional code.
     * Similar functionality to python decorators
     * @param  {object} context         Object for which we want the methods to become wrapped
     * @param  {function} wrapper       Function that executes the additional code
     * @param  {...string} methodName   Name of method(s) to wrap
     */
    wrapMethodsWith(context, wrapper, ...methodNames) {
        _.each(methodNames, methodName => {
            const method = context[methodName];
            if (typeof method == 'function') {
                context[methodName] = wrapper(method);
            }
        });
    },

    /**
     * Concatenates the results of several promises
     * Rejected promises are ignored except if all promises are rejected - in this case
     * the returned promise is rejected with the reason for the rejection of the first promise
     * @param  {array<promise<array>>} promises
     * @return {promise<array>}          [description]
     */
    concatPromiseResults(promises) {
        promises = _.invoke(promises, 'reflect');
        return Promise.all(promises).then(results => {
            //results is array of PromiseInspection
            const fulfilled = _.chain(results)
                .map(r => {
                    if (r.isFulfilled()) return r.value();
                })
                .compact()
                .value();

            if (promises.length > 0 && _.isEmpty(fulfilled)) throw results[0].reason();
            else return _.flatten(fulfilled);
        });
    },

    /**
     * Converts a given relative url to account for option baseUrl
     * If the given url is absolute it is returned as is
     * @return {url}
     */
    convertUrl(url) {
        const regexp = /(http|https):\/\//;
        const match = url.match(regexp);
        if (match) return url;
        //absolute url
        else return myw.baseUrl + url;
    },

    /**
     * Adjust a relative url in a given icon definition to account for myw.baseUrl
     * @return {object} iconDef
     */
    fixIconDefPath(iconDef) {
        if (myw.baseUrl && iconDef.iconUrl)
            return _.extend({}, iconDef, { iconUrl: myw.Util.convertUrl(iconDef.iconUrl) });
        else return iconDef;
    },

    /**
     * Get the url specified parameter with a given name
     * @param  {string} name name of the parameter
     * @return {string}      Parameter value
     */
    getUrlParam(name) {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');

        const regexS = '[\\?&]' + name + '=([^&#]*)';
        const regex = new RegExp(regexS);
        const results = regex.exec(window.location.href);

        if (results === null) {
            return '';
        } else {
            return results[1];
        }
    },

    /**
     * Copies a given value to the operating system's clipboard
     * @param  {string|number} data     data to copy
     */
    copyToClipboard(data) {
        // Create a dummy input to copy the string array inside it
        const dummy = document.createElement('input');
        // Add it to the document
        document.body.appendChild(dummy);
        // Set its ID
        dummy.setAttribute('id', 'dummy_id');
        // Output the array into it
        document.getElementById('dummy_id').value = data;
        // workaround for ios platform
        if (this.isIOS) {
            //Create a range to select the desired element and add it to the window's selection.
            const range = document.createRange();
            dummy.contentEditable = true;
            dummy.readOnly = false;
            range.selectNodeContents(dummy);
            const s = window.getSelection();
            s.removeAllRanges();
            s.addRange(range);
            // A big number, to cover anything that could be inside the elemen
            dummy.setSelectionRange(0, 999999);
        } else {
            // Select it
            dummy.select();
        }
        // Copy its contents
        document.execCommand('copy');
        // Remove it as its not needed anymore
        document.body.removeChild(dummy);
    },

    /**
     * Return latLng as a neatly formatted string
     * @param {L.Latlng} latlng
     * @param {int} n_dp (optional)
     */
    // ENH: Support other formats e.g. deg/min/sec
    formatLatLng(latlng, n_dp) {
        let lat_ch, lng_ch, lat_str, lng_str;

        // Determine suffices
        lat_ch = latlng.lat < 0 ? 'S' : 'N';
        lng_ch = latlng.lng < 0 ? 'W' : 'E';

        // Build string
        if (n_dp) {
            lat_str = Math.abs(latlng.lat).toFixed(n_dp);
            lng_str = Math.abs(latlng.lng).toFixed(n_dp);
        } else {
            lat_str = Math.abs(latlng.lat).toString();
            lng_str = Math.abs(latlng.lng).toString();
        }

        return lat_str + ' ' + lat_ch + ' , ' + lng_str + ' ' + lng_ch;
    },

    /**
     * Parses a string to identify a lat/lng coordinate
     * Returns undefined if it couldn't find a match
     * @param  {string} str
     * @return {object} With properties lat and lng
     */
    parseLatLng(str) {
        let lat, lng, match;

        // Try unqualified lat,lng pair e.g. 1.234,34.12
        match = /^\s*(\+|\-|)(\d+)(\.\d+|)\s*,\s*(\+|\-|)(\d+)(\.\d+|)\s*$/.exec(str);
        if (match) {
            lat = Number(match[1] + match[2] + match[3]);
            lng = Number(match[4] + match[5] + match[6]);
            return { lat, lng };
        }

        // Try qualified lat,lng pair e.g. 1.234N,34.12E
        match = /^\s*(\d+)(\.\d+|)\s*(N|S)\s*,\s*(\d+)(\.\d+|)\s*(E|W)\s*$/i.exec(str);
        if (match) {
            lat = Number(match[1] + match[2]);
            lng = Number(match[4] + match[5]);
            if (match[3].toUpperCase() == 'S') lat *= -1;
            if (match[6].toUpperCase() == 'W') lng *= -1;
            return { lat, lng };
        }

        // Try qualified lng,lat pair e.g. 1.234E,34.12N
        match = /^\s*(\d+)(\.\d+|)\s*(E|W)\s*,\s*(\d+)(\.\d+|)\s*(N|S)\s*$/i.exec(str);
        if (match) {
            lng = Number(match[1] + match[2]);
            lat = Number(match[4] + match[5]);
            if (match[3].toUpperCase() == 'W') lng *= -1;
            if (match[6].toUpperCase() == 'S') lat *= -1;
            return { lat, lng };
        }
    },

    /**
     *  Converts a base64 string into a Blob
     *  @Returns Blob
     */
    b64toBlob: function(b64Data, contentType, sliceSize) {
        contentType = contentType || '';
        sliceSize = sliceSize || 512;

        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: contentType });
    },

    /**
     * Converts decimal degrees to radians
     * @param {float}   degrees angle in decimal degrees
     * @returns {float} angle in radians
     */
    toRadians(degrees) {
        return (degrees * Math.PI) / 180;
    },

    /**
     * Converts  to radians decimal degrees
     * @param {float} radians angle in radians
     * @returns {float} angle in decimal degrees
     */
    toDegrees(radians) {
        return (radians * 180) / Math.PI;
    },

    /**
     * Create a new Point that is a set length and direction from
     * an initial point.
     * @param {L.Point} point    initial point
     * @param {float} length     length in pixels
     * @param {float} direction  direction in decimal degrees
     * @return {L.Point} new point a set distance/direction away from initial point
     */
    createPointByVector(point, length, direction) {
        const theta = myw.Util.toRadians(direction);

        const dX = length * Math.sin(theta);
        const dY = length * -Math.cos(theta);
        return new L.Point(point.x + dX, point.y + dY);
    },

    /**
     * Returns input string to Title case string (I'm a little teapot -> I'm A Little Teapot)
     * @param {string} string
     */
    toTitleCase(string) {
        return string
            .toLowerCase()
            .split(' ')
            .map(word => word.replace(word[0], word[0].toUpperCase()))
            .join(' ');
    },

};

export default myw.Util;
